import React, { useState, useEffect } from 'react';
import { supabase } from '../../services/supabase.ts';
import { BillFormData } from '../../types/bill.ts';
import { Property } from '../../types/property.ts';
import { formatCurrency } from '../../utils/formatters.ts';
import '../../styles/Bills.css';

interface BillTenant {
  id: string;
  full_name: string;
  property_id: string;
  monthly_rent: number;
}

interface BillFormProps {
  onClose: (shouldRefresh?: boolean) => void;
}
const BillForm: React.FC<BillFormProps> = ({ onClose }) => {
  const [loading, setLoading] = useState(false);
  const [tenants, setTenants] = useState<BillTenant[]>([]);
  const [properties, setProperties] = useState<Property[]>([]);
  const [formData, setFormData] = useState<BillFormData>({
    month: new Date().toISOString().split('T')[0],
    status: 'Unpaid', // Default to Unpaid
    tenant_id: '',
    property_id: '',
    amount: 0,
    paid_amount: 0,
    due_date: new Date().toISOString().split('T')[0],
  });
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});
  const [wasSubmitAttempted, setWasSubmitAttempted] = useState(false);
  const [maxAllowedAmount, setMaxAllowedAmount] = useState<number>(0);
  const [formEnabled, setFormEnabled] = useState(false);

  const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  const [selectedMonth, setSelectedMonth] = useState(() => {
    const today = new Date();
    // Get previous month (0-11)
    const prevMonth = today.getMonth() === 0 ? 11 : today.getMonth() - 1;
    return prevMonth;
  });

  const [selectedYear, setSelectedYear] = useState(() => {
    const today = new Date();
    // If we're in January, previous month was December of last year
    return today.getMonth() === 0 ? today.getFullYear() - 1 : today.getFullYear();
  });

  useEffect(() => {
    fetchTenants();
    fetchProperties();
  }, []);

  const fetchTenants = async () => {
    const { data } = await supabase
      .from('tenants')
      .select('id, full_name, property_id, monthly_rent')
      .eq('status', 'Active');
    if (data) setTenants(data);
  };

  const fetchProperties = async () => {
    const { data } = await supabase
      .from('properties')
      .select('*')
      .eq('status', 'Rented');
    if (data) setProperties(data);
  };

  const handleTenantChange = (tenantId: string) => {
    const tenant = tenants.find(t => t.id === tenantId);
    if (tenant) {
      setFormData({
        ...formData,
        tenant_id: tenant.id,
        property_id: tenant.property_id,
        amount: tenant.monthly_rent
      });
      setMaxAllowedAmount(tenant.monthly_rent);
      setFormEnabled(true); // Enable form when tenant is selected
    } else {
      setFormData({
        ...formData,
        tenant_id: '',
        property_id: '',
        amount: 0
      });
      setMaxAllowedAmount(0);
      setFormEnabled(false); // Disable form when no tenant is selected
    }
  };

  const handleAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    // Only allow numbers and empty string
    if (value === '' || /^\d*$/.test(value)) {
      const numValue = parseInt(value) || 0;
      const limitedValue = Math.min(numValue, maxAllowedAmount);
      setFormData({
        ...formData,
        amount: limitedValue
      });
      clearError('amount');
    }
  };

  const handleNotesChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const text = e.target.value;
    if (text.length <= 50) {
      setFormData({ ...formData, notes: text });
    }
  };

  const validateForm = () => {
    const errors: Record<string, string> = {};
    
    if (!formData.tenant_id) errors.tenant_id = 'This field is required';
    if (!formData.property_id) errors.property_id = 'This field is required';
    if (!formData.month) errors.month = 'This field is required';
    if (!formData.amount || formData.amount <= 0) errors.amount = 'Please enter a valid amount';
    if (!formData.due_date) errors.due_date = 'This field is required';

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const clearError = (field: string) => {
    if (wasSubmitAttempted) {
      setValidationErrors(prev => {
        const updated = { ...prev };
        delete updated[field];
        return updated;
      });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setWasSubmitAttempted(true);
    
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    try {
      const { error } = await supabase
        .from('bills')
        .insert([formData]);

      if (error) throw error;
      onClose(true); // Pass true to trigger refresh only on successful creation
    } catch (error) {
      console.error('Error creating bill:', error);
      alert('Failed to create bill');
    } finally {
      setLoading(false);
    }
  };

  // Add handler to prevent scroll propagation
  const handleModalWheel = (e: React.WheelEvent) => {
    e.stopPropagation();
  };

  // Add ESC key handler
  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose(false); // Pass false to skip refresh on ESC
      }
    };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [onClose]);

  // Update formData when month/year changes
  useEffect(() => {
    const date = new Date(selectedYear, selectedMonth + 1, 1);
    setFormData(prev => ({
      ...prev,
      month: date.toISOString().split('T')[0]
    }));
  }, [selectedMonth, selectedYear]);

  const handleYearChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (value === '' || /^\d{0,4}$/.test(value)) {
      const yearNum = parseInt(value) || 0;
      setSelectedYear(yearNum);
      clearError('month');
    }
  };

  return (
    <div className="modal" onClick={() => onClose(false)}>
      <div 
        className="bill-form" 
        onClick={(e) => e.stopPropagation()}
        onWheel={handleModalWheel}
      >
        <div className="modal-header">
          <h2>Create New Bill</h2>
          <button 
            className="close-button" 
            onClick={() => onClose(false)} // Pass false to skip refresh on cancel
          >
            &times;
          </button>
        </div>
        <form onSubmit={handleSubmit} noValidate>
          <div className="form-group">
            <label>Tenant *</label>
            <select
              value={formData.tenant_id}
              onChange={(e) => {
                handleTenantChange(e.target.value);
                clearError('tenant_id');
              }}
              required
            >
              <option value="">Select Tenant</option>
              {tenants.map((tenant) => (
                <option key={tenant.id} value={tenant.id}>
                  {tenant.full_name}
                </option>
              ))}
            </select>
            {validationErrors.tenant_id && (
              <span className="validation-message">{validationErrors.tenant_id}</span>
            )}
          </div>

          <div className="form-group">
            <label>Property</label>
            <select
              value={formData.property_id}
              disabled={true}
              required
            >
              <option value="">Select Property</option>
              {properties.map((property) => (
                <option key={property.id} value={property.id}>
                  {property.name}
                </option>
              ))}
            </select>
            {validationErrors.property_id && (
              <span className="validation-message">{validationErrors.property_id}</span>
            )}
          </div>

          <div className="form-group month-year-group">
            <label>Bill Period *</label>
            <div className="month-year-inputs">
              <select
                value={selectedMonth}
                onChange={(e) => {
                  setSelectedMonth(parseInt(e.target.value));
                  clearError('month');
                }}
                className="month-select"
                disabled={!formEnabled}
              >
                {months.map((month, index) => (
                  <option key={month} value={index}>
                    {month}
                  </option>
                ))}
              </select>
              <input
                type="text"
                value={selectedYear || ''}
                onChange={handleYearChange}
                className="year-input"
                maxLength={4}
                disabled={!formEnabled}
                placeholder="YYYY"
              />
            </div>
            {validationErrors.month && (
              <span className="validation-message">{validationErrors.month}</span>
            )}
          </div>

          <div className="form-group">
            <label>Amount *</label>
            <input
              type="text"
              value={formData.amount}
              onChange={handleAmountChange}
              placeholder="Enter amount"
              maxLength={7} // To prevent typing more than 7 digits
              required
              disabled={!formEnabled}
            />
            {formEnabled && maxAllowedAmount > 0 && (
              <span className="form-hint">
                Maximum allowed: {formatCurrency(maxAllowedAmount)}
              </span>
            )}
            {validationErrors.amount && (
              <span className="validation-message">{validationErrors.amount}</span>
            )}
          </div>

          <div className="form-group">
            <label>Due Date *</label>
            <input
              type="date"
              value={formData.due_date}
              onChange={(e) => {
                // Validate year is not more than 4 digits
                const date = new Date(e.target.value);
                const year = date.getFullYear();
                if (year > 9999) {
                  return; // Don't update if year is more than 4 digits
                }
                setFormData({ ...formData, due_date: e.target.value });
                clearError('due_date');
              }}
              max="9999-12-31" // Add max date attribute
              required
              disabled={!formEnabled}
            />
            {validationErrors.due_date && (
              <span className="validation-message">{validationErrors.due_date}</span>
            )}
          </div>
          <div className="form-group">
            <label>Notes</label>
            <textarea
              value={formData.notes || ''}
              onChange={handleNotesChange}
              rows={3}
              disabled={!formEnabled}
            />
            <span className="form-hint">
              {`${(formData.notes || '').length} of 50 characters`}
            </span>
          </div>

          <div className="form-actions">
            <button type="submit" className="btn-primary" disabled={loading || !formEnabled}>
              {loading ? 'Creating...' : 'Create Bill'}
            </button>
            <button 
              type="button" 
              className="btn-secondary" 
              onClick={() => onClose(false)} // Pass false to skip refresh on cancel
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default BillForm;